import React, { useMemo, useReducer, useRef } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'next-i18next';
import Spacer from '@/components/ui-components/Spacer';
import ChipButton from '@/components/ui-components/ChipButton';
import useMoneyFormat from '@/hooks/useMoneyFormat';
import useDerivedChartData, {
  ControlStateType,
  RawChartDataType,
  SVG_HEIGHT,
  SVG_WIDTH,
  TimeFrameType,
} from './useDerivedChartData';
import Typography from '../../components/Typography';

const useStyles = makeStyles((theme) => ({
  root: {},
  chipArray: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  dot: {
    display: 'flex',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: theme.spacing(0.4),
    marginTop: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
  },
  chipArrayBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',

    '& > *': {
      margin: theme.spacing(0.5),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  bottomActions: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'space-between',
    },
  },

  graphInfoItem: {
    display: 'flex',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },

}));

interface Props {
  data: RawChartDataType;
}

const defaultProps = {};

const AVAILABLE_TIME_FRAMES: TimeFrameType[] = [
  {
    i18nKey: '1W',
    minDataLength: 1000 * 3600 * 24 * 7 * 2, // 2 weeks
    timeFrameLength: 1000 * 3600 * 24 * 7,
    requiredIntervalDesktop: 'daily',
    requiredIntervalMobile: 'daily',
  },
  {
    i18nKey: '1M',
    minDataLength: 1000 * 3600 * 24 * 7 * 6, // 6 weeks
    timeFrameLength: 1000 * 3600 * 24 * 30,
    requiredIntervalDesktop: 'daily',
    requiredIntervalMobile: 'weekly',
  },
  {
    i18nKey: '3M',
    minDataLength: 1000 * 3600 * 24 * 7 * 20, // 20 weeks
    timeFrameLength: 1000 * 3600 * 24 * 90,
    requiredIntervalDesktop: 'weekly',
    requiredIntervalMobile: 'monthly',
  },
  {
    i18nKey: '1Y',
    minDataLength: 1000 * 3600 * 24 * 7 * 70, // 70 weeks
    timeFrameLength: 1000 * 3600 * 24 * 365,
    requiredIntervalDesktop: 'monthly',
    requiredIntervalMobile: 'monthly',
  },
  {
    i18nKey: 'ALL',
    minDataLength: 0,
    timeFrameLength: Infinity,
    requiredIntervalDesktop: 'daily',
    requiredIntervalMobile: 'daily',
  },
];

const Graph = ({ data }: Props) => {
  const classes = useStyles();
  const ref = useRef<SVGSVGElement | null>(null);
  const theme = useTheme();
  const { t, i18n } = useTranslation('markets');
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const availableTimeFrames = useMemo<TimeFrameType[]>(
    () => AVAILABLE_TIME_FRAMES.filter((x) => x.minDataLength < data.chartLength),
    [data],
  );

  // state
  // 1. selected graph type: raised/daily
  // 2. time frame
  const initialState: ControlStateType = {
    selectedChartType: 'ACCUMULATIVE',
    selectedTimeFrame: availableTimeFrames[availableTimeFrames.length - 1],
  };

  function reducer(state, action) {
    switch (action.type) {
      case 'SELECT_CHART_TYPE':
        // if we have selected 'ALL' and we want to switch to the 'DELTA' view,
        // switch to largest view instead if we have other views available
        if (
          action.payload === 'DELTA'
          && state.selectedTimeFrame.i18nKey === 'ALL'
          && availableTimeFrames.length > 1
        ) {
          return {
            ...state,
            selectedTimeFrame:
              availableTimeFrames[availableTimeFrames.length - 2],
            selectedChartType: action.payload,
          };
        }
        // otherwise just switch selected chart
        return { ...state, selectedChartType: action.payload };
      case 'SELECT_TIME_FRAME':
        return {
          ...state,
          selectedTimeFrame: availableTimeFrames.find(
            (x) => x.i18nKey === action.payload,
          ),
        };
      default:
        throw new Error();
    }
  }

  const [controlState, dispatch] = useReducer(reducer, initialState);

  //   derived data
  // 3. --> computed: graph data
  const { currentSelection, hasTestingTheWatersAccumulative } = useDerivedChartData(
    ref,
    data,
    controlState,
    isMobile,
  );

  const formatDate = (format: string, date: Date) => {
    switch (format) {
      case '1W':
      case '1M':
      case '3M':
        return date.toLocaleDateString(i18n.language, {
          weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: 'numeric',
        });
      case '1Y':
      case 'ALL':
        return date.toLocaleDateString(i18n.language, {
          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        });
      default:
        return date.toLocaleDateString(i18n.language, {
          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        });
    }
  };

  return (
    <>
      <Typography variant="header">{t('markets.detailView.commitments.title')}</Typography>
      <Spacer spacing={2} />
      {/* <p>{data.chartLength}</p>
      <p>{data.lastTimestamp}</p>
      <pre>{JSON.stringify(controlState, null, 2)}</pre>
      <pre>{JSON.stringify(accumulativeCommitments, null, 2)}</pre> */}

      <Grid container xs={12} className={classes.header}>
        {currentSelection && (
          <Grid
            item
            xs={12}
            sm={5}
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <Grid item xs={7}>
              <Typography variant="base">
                { formatDate(controlState.selectedTimeFrame.i18nKey,
                  currentSelection.x)}
              </Typography>
              <div style={{ display: 'flex' }}>
                {controlState.selectedChartType === 'DELTA' && (
                  <div
                    className={classes.dot}
                    style={{ backgroundColor: theme.palette.primary.main }}
                  />
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="title">
                    {useMoneyFormat(controlState.selectedChartType === 'DELTA' ? currentSelection.deltaCommitments : currentSelection.raised, i18n, 'USD', 'currency',
                      'symbol',
                      0,
                      2)}
                  </Typography>
                  <Typography
                    variant="secondary"
                    style={{ color: theme.palette.greyScale.dark }}
                  >
                    {t('markets.detailView.commitments.raised')}
                  </Typography>
                </div>
              </div>
            </Grid>
            {controlState.selectedChartType === 'DELTA' && (
              <Grid item xs={5} style={{ display: 'flex' }}>
                <div
                  className={classes.dot}
                  style={{ backgroundColor: theme.palette.secondary.main }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="title">
                    {currentSelection.deltaInvestors}
                  </Typography>
                  <Typography
                    variant="secondary"
                    style={{ color: theme.palette.greyScale.dark }}
                  >
                    {t('markets.detailView.commitments.investors')}
                  </Typography>
                </div>
              </Grid>
            )}
          </Grid>
        )}

        <Grid item sm={6} className={classes.chipArray}>
          <ChipButton
            isActive={controlState.selectedChartType === 'ACCUMULATIVE'}
            onClick={() => dispatch({
              type: 'SELECT_CHART_TYPE',
              payload: 'ACCUMULATIVE',
            })}
            label={t('markets.detailView.commitments.moneyRaised')}
          />
          <ChipButton
            isActive={controlState.selectedChartType === 'DELTA'}
            onClick={() => dispatch({
              type: 'SELECT_CHART_TYPE',
              payload: 'DELTA',
            })}
            label={t('markets.detailView.commitments.dailyInvestments')}
          />
        </Grid>
      </Grid>

      {/* graph */}
      <div
        style={{
          width: '100%',
          paddingTop: '55%',
          position: 'relative',
          marginBottom: 30,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <svg
            ref={ref}
            width="100%"
            height="100%"
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          />
        </div>
      </div>

      {/* bottom actions */}
      <div className={classes.bottomActions}>
        <div style={{ display: 'flex' }}>
          <div className={classes.graphInfoItem}>
            <div
              className={classes.dot}
              style={{ backgroundColor: theme.palette.primary.main }}
            />
            <Typography
              variant="secondary"
              style={{
                color: theme.palette.greyScale.dark,
                marginLeft: theme.spacing(0.5),
                marginTop: theme.spacing(0.2),
              }}
            >
              {t('markets.detailView.commitments.raised')}
            </Typography>
          </div>
          {controlState.selectedChartType === 'DELTA'
             && (
             <div className={classes.graphInfoItem}>
               <div
                 className={classes.dot}
                 style={{ backgroundColor: theme.palette.secondary.main }}
               />
               <Typography
                 variant="secondary"
                 style={{
                   color: theme.palette.greyScale.dark,
                   marginLeft: theme.spacing(0.5),
                   marginTop: theme.spacing(0.2),
                 }}
               >
                 {t('markets.detailView.commitments.investors')}
               </Typography>
             </div>
             )}

          {hasTestingTheWatersAccumulative && (
            <div className={classes.graphInfoItem}>
              <div
                className={classes.dot}
                style={{ backgroundColor: theme.palette.greyScale.main }}
              />
              <Typography
                variant="secondary"
                style={{
                  color: theme.palette.greyScale.dark,
                  marginLeft: theme.spacing(0.5),
                  marginTop: theme.spacing(0.2),
                }}
              >
                {t('markets.detailView.commitments.testingTheWaters')}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.chipArray}>
          {availableTimeFrames.map((tf) => {
            const isActive = controlState.selectedTimeFrame.i18nKey === tf.i18nKey;
            return (
              <ChipButton
                key={tf.i18nKey}
                isActive={isActive}
                onClick={() => dispatch({ type: 'SELECT_TIME_FRAME', payload: tf.i18nKey })}
                label={tf.i18nKey}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

Graph.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default Graph;
